import type { CajaReporte, CajaReporteFormaPago } from './metricsTypes';

/** Totales del reporte de caja, ya consolidados si se miran varias clínicas. */
export interface CajaReporteTotals {
  ingresos: number;
  egresos: number;
  comisiones: number;
  neto: number;
  /** Ingresos + comisiones: la comisión es un recargo al paciente, no un descuento. */
  totalCobrado: number;
  movimientos: number;
  porFormaPago: CajaReporteFormaPago[];
}

const EMPTY_KEY = '__sin_forma_pago__';

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Consolida uno o varios reportes de caja en un único set de totales.
 *
 * El desglose se agrupa por **nombre** de forma de pago, no por id: cada clínica vive en
 * su propia BD, así que un mismo `formaPagoId` puede significar cosas distintas entre
 * clínicas. Al consolidar, `formaPagoId` sólo se conserva si todas las filas del grupo
 * comparten el mismo id.
 */
export function aggregateCajaReportes(reportes: CajaReporte[]): CajaReporteTotals {
  const grupos = new Map<string, CajaReporteFormaPago & { idsDistintos: boolean }>();

  for (const reporte of reportes) {
    for (const fila of reporte.porFormaPago) {
      const key = fila.formaPago ?? EMPTY_KEY;
      const actual = grupos.get(key);

      if (!actual) {
        grupos.set(key, { ...fila, idsDistintos: false });
        continue;
      }

      actual.ingresos += fila.ingresos;
      actual.egresos += fila.egresos;
      actual.comisiones += fila.comisiones;
      actual.movimientos += fila.movimientos;
      if (actual.formaPagoId !== fila.formaPagoId) {
        actual.idsDistintos = true;
      }
    }
  }

  const porFormaPago = [...grupos.values()]
    .map(({ idsDistintos, ...fila }) => ({
      ...fila,
      formaPagoId: idsDistintos ? null : fila.formaPagoId,
      ingresos: round2(fila.ingresos),
      egresos: round2(fila.egresos),
      comisiones: round2(fila.comisiones),
    }))
    .sort((a, b) => b.ingresos - a.ingresos);

  const sum = (field: 'ingresos' | 'egresos' | 'comisiones' | 'movimientos') =>
    porFormaPago.reduce((total, fila) => total + fila[field], 0);

  const ingresos = round2(sum('ingresos'));
  const egresos = round2(sum('egresos'));
  const comisiones = round2(sum('comisiones'));
  const neto = round2(ingresos - egresos);

  return {
    ingresos,
    egresos,
    comisiones,
    neto,
    totalCobrado: round2(ingresos + comisiones),
    movimientos: sum('movimientos'),
    porFormaPago,
  };
}
